import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, ChevronDown, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useCurrency } from '../context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Highlight = ({ text, q }) => {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (!q || i < 0) return text;
  return <>{text.slice(0, i)}<strong className="font-semibold text-neutral-900 dark:text-white">{text.slice(i, i + q.length)}</strong>{text.slice(i + q.length)}</>;
};

const Suggestions = ({ items, q, hi, setHi, onPick, onSeeAll }) => {
  const { fmt } = useCurrency();
  return (
  <div data-testid="search-suggestions" role="listbox" className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] shadow-2xl overflow-hidden">
    {items.map((p, i) => (
      <button type="button" role="option" aria-selected={hi === i} key={p.id} data-testid={`search-suggestion-${p.id}`}
        onMouseEnter={() => setHi(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(p)}
        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${hi === i ? 'bg-brand-soft' : ''}`}>
        <span className="w-10 h-10 shrink-0 rounded-md bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
          {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13.5px] text-neutral-700 dark:text-neutral-200 truncate"><Highlight text={p.name} q={q} /></span>
          <span className="block text-[11.5px] text-neutral-500 truncate">{[p.strength, p.catLabel].filter(Boolean).join(' · ')}</span>
        </span>
        <span className="text-[13px] font-semibold text-neutral-900 dark:text-neutral-100">{fmt(p.price)}</span>
      </button>
    ))}
    <button type="button" data-testid="search-see-all" onMouseDown={(e) => e.preventDefault()} onClick={onSeeAll}
      className={`w-full flex items-center gap-2 px-4 py-2.5 border-t border-neutral-100 dark:border-neutral-800 text-[12.5px] text-brand font-medium ${hi === items.length ? 'bg-brand-soft' : ''}`}>
      <Search size={13} /> See all results for “{q}”
    </button>
  </div>
  );
};

const HeaderSearch = () => {
  const { search, activeCat, runSearch, setQuickView } = useApp();
  const [text, setText] = useState(search);
  const [cat, setCat] = useState(activeCat === 'top' ? 'all' : activeCat);
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const reqId = useRef(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    axios.get(`${API}/categories`).then(({ data }) => setCats(data || [])).catch(() => {});
  }, []);
  useEffect(() => { setText(search); }, [search]);
  useEffect(() => { if (activeCat !== 'top') setCat(activeCat); }, [activeCat]);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) { setItems([]); return undefined; }
    const id = ++reqId.current;
    const t = setTimeout(() => {
      axios.get(`${API}/products/suggest`, { params: { q, cat } })
        .then(({ data }) => { if (id === reqId.current) { setItems(data || []); setHi(-1); } })
        .catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [text, cat]);

  const go = (q) => { setOpen(false); runSearch(q, cat); if (pathname !== '/') navigate('/'); };
  const pick = (p) => { setOpen(false); setQuickView(p); };
  const submit = (e) => {
    e.preventDefault();
    if (open && hi >= 0 && hi < items.length) pick(items[hi]);
    else go(text.trim());
  };
  const onKey = (e) => {
    if (!open || !items.length) return;
    const max = items.length;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => (h >= max ? 0 : h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => (h <= 0 ? max : h - 1)); }
    else if (e.key === 'Escape') setOpen(false);
  };
  const clear = () => { setText(''); setItems([]); runSearch('', cat); };
  const catLabel = cat === 'all' ? 'All' : (cats.find(c => c.id === cat)?.label || 'All');
  const showList = open && items.length > 0 && text.trim().length >= 2;

  return (
    <form onSubmit={submit} role="search" data-testid="header-search-form" className="relative w-full">
      <div className="flex w-full items-stretch h-11 rounded-full border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-[#141416] overflow-hidden focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/25 transition-[box-shadow,border-color]">
        <div className="relative shrink-0 border-r border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
          <span className="pointer-events-none flex h-full items-center gap-1 pl-4 pr-3 text-[12.5px] text-neutral-700 dark:text-neutral-200 max-w-[92px] sm:max-w-[150px]">
            <span className="truncate">{catLabel}</span><ChevronDown size={13} className="shrink-0 text-neutral-400" />
          </span>
          <select data-testid="header-search-category" aria-label="Search category" value={cat} onChange={(e) => setCat(e.target.value)}
            className="absolute inset-0 w-full opacity-0 cursor-pointer">
            <option value="all">All categories</option>
            {cats.filter(c => c.id !== 'all' && c.id !== 'top').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <input data-testid="header-search-input" value={text} autoComplete="off"
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onKeyDown={onKey}
          placeholder="Search products, e.g. Finasteride" aria-label="Search products" aria-expanded={showList}
          className="flex-1 min-w-0 bg-transparent px-4 text-[14px] text-neutral-900 dark:text-neutral-100 outline-none placeholder:text-neutral-400" />
        {text && (
          <button type="button" data-testid="header-search-clear" onClick={clear} aria-label="Clear search" className="px-2 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"><X size={15} /></button>
        )}
        <button type="submit" data-testid="header-search-submit" aria-label="Search" className="btn-brand shrink-0 px-5 grid place-items-center">
          <Search size={17} />
        </button>
      </div>
      {showList && <Suggestions items={items} q={text.trim()} hi={hi} setHi={setHi} onPick={pick} onSeeAll={() => go(text.trim())} />}
    </form>
  );
};

export default HeaderSearch;
